/**
 * src/lib/gantt/useSchemaOptions.js — Schema Options Hook for Gantt Planner
 * AC Nautitech
 *
 * Consumes stages, departments, and project types from AppDataContext
 * instead of making independent Supabase queries.
 * Module-level cache kept for imperative access via getSchemaOptions().
 */

import { useMemo } from 'react';
import { useAppDataContext } from '../AppDataContext';
import { DEPARTMENTS, normalizeDepartment } from '../constants/departments';

let cachedOptions = null;

export function useSchemaOptions() {
  const { stages: rawStages, projectTypes: rawProjectTypes, departments: rawDepartments } = useAppDataContext();

  const options = useMemo(() => {
    const stages = (rawStages || []).map(s => s.label_en || s.stage_name || s.name || '');
    const projectTypes = (rawProjectTypes || []).map(t => t.type_name || t.label_en || t.name || '');
    const departments = (rawDepartments || [])
      .map(d => normalizeDepartment(d.name) || normalizeDepartment(d.name_es))
      .filter(Boolean);

    const result = {
      stages,
      departments: departments.length ? [...new Set(departments)] : DEPARTMENTS,
      statuses: ['todo', 'in_progress', 'blocked', 'done'],
      projectTypes,
      workTypes: [],
      stagesByProjectType: {},
      loading: false,
    };
    cachedOptions = result;
    return result;
  }, [rawStages, rawProjectTypes, rawDepartments]);

  return options;
}

/**
 * Imperative access for non-React code (e.g., utility functions).
 * Returns null if not yet loaded.
 */
export function getSchemaOptions() {
  return cachedOptions;
}

/**
 * Force refresh — call after schema changes.
 */
export function invalidateSchemaCache() {
  cachedOptions = null;
}
